<script>
import getErrorMessage from '@/modules/server-errors';
import { basename, extname, replaceFilename, safeFilename } from '@/modules/file-utilities';

export default {
  name: 'RenameDialog',
  props: {
    site: Object
  },
  data() {
    return {
      dialogVisible: false,
      form: {
        name: '',
        filename: '',
        extension: ''
      },
      invalid: [],
      page: {},
      renaming: false
    };
  },
  methods: {
    // Prevents unsafe characters from being entered into filename inputs
    handleFilenameChange() {
      this.form.filename = safeFilename(this.form.filename);
    },

    // Opens the dialog
    open(page) {
      this.page = Object.assign({}, page);
      this.form.name = this.page.name;
      this.form.extension = extname(this.page.path);
      this.form.filename = basename(this.page.path, this.form.extension);
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.name.select());
    },

    // Closes the dialog
    close() {
      this.dialogVisible = false;
    },

    // Replaces a filename in a path
    replaceFilename(path, newFilename) {
      return replaceFilename(path, newFilename);
    },

    // Handles for submission
    submit() {
      const name = this.form.name;
      const filename = safeFilename(this.form.filename);
      const path = replaceFilename(this.page.path, filename);
      const pageId = this.page.id;

      // Check for invalid fields
      this.invalid = [];
      if (!name) this.invalid.push('name');
      if (!filename) this.invalid.push('filename');
      if (this.invalid.length) {
        this.$notify({
          type: 'error',
          title: this.$t('Rename'),
          message: this.$t('Please correct the highlighted errors.')
        });
        return;
      }

      // Rename the page
      this.renaming = true;
      this.$http
        .put(`/sites/${this.site.id}/pages/${pageId}`, { name, path })
        .then(res => {
          this.$emit('complete', res.data.page);
          this.close();
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Rename'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.renaming = false));
    }
  }
};
</script>

<template>
  <el-dialog :title="$t('Rename')" :visible.sync="dialogVisible">
    <el-form @submit.native.prevent="submit">
      <!-- Name -->
      <el-form-item :class="{ 'is-error': invalid.includes('name') }" :label="$t('Name')">
        <el-input ref="name" v-model="form.name" type="text" prefix-icon="el-icon-fa-tag" auto-complete="off" />
      </el-form-item>

      <!-- Filename -->
      <el-form-item :class="{ 'is-error': invalid.includes('filename') }" :label="$t('Filename')">
        <el-input
          v-model="form.filename"
          type="text"
          auto-complete="off"
          prefix-icon="el-icon-fa-file"
          @change="handleFilenameChange"
        >
          <template slot="append">
            {{ form.extension }}
          </template>
        </el-input>
      </el-form-item>

      <!-- Change filename warning -->
      <el-alert
        v-if="replaceFilename(page.path, form.filename) !== page.path"
        :closable="false"
        :title="$t('Warning')"
        :description="
          $t(
            'Links to this page will stop working. You should only change the filename if you\'re sure nothing is linking to this page.'
          )
        "
        type="warning"
      />

      <!-- Allow the form to submit when enter is pressed inside a field -->
      <el-button v-show="false" native-type="submit" />
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="close">
        {{ $t('Cancel') }}
      </el-button>
      <el-button :loading="renaming" type="primary" @click="submit">
        {{ $t('Rename') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

// No styles yet
</style>
